// ------------------------------------------------------------
// Cliente único de la API.
//
// Todas las llamadas del sistema pasan por aquí. Eso concentra en un
// solo lugar la URL, el envío de la cookie de sesión y el manejo de
// errores: si el servidor responde con un error, se lanza una excepción
// con el mensaje que él mismo devolvió.
// ------------------------------------------------------------

// La URL sale de .env.development (npm start) o .env.production (npm run build).
// El valor de respaldo mantiene el comportamiento anterior si faltara el archivo.
export const API_URL =
  process.env.REACT_APP_API_URL || "https://rustiko.mng.cr/api.php";

class ApiError extends Error {
  constructor(mensaje, status) {
    super(mensaje);
    this.name = "ApiError";
    this.status = status;
  }
}

/**
 * Arma la URL con la acción y los parámetros, descartando los vacíos.
 */
function construirUrl(accion, params = {}) {
  const query = new URLSearchParams({ action: accion });
  Object.entries(params).forEach(([clave, valor]) => {
    if (valor !== undefined && valor !== null && valor !== "") {
      query.append(clave, valor);
    }
  });
  return `${API_URL}?${query.toString()}`;
}

/**
 * Hace la petición y devuelve el JSON ya convertido.
 * Lanza ApiError si el servidor responde con un código de error.
 */
async function pedir(accion, { metodo = "GET", params, cuerpo } = {}) {
  const opciones = { method: metodo, credentials: "include" };

  if (cuerpo !== undefined) {
    opciones.headers = { "Content-Type": "application/json" };
    opciones.body = JSON.stringify(cuerpo);
  }

  const respuesta = await fetch(construirUrl(accion, params), opciones);

  // Algunas respuestas pueden venir vacías; en ese caso no hay JSON que leer.
  let datos = null;
  const texto = await respuesta.text();
  if (texto) {
    try {
      datos = JSON.parse(texto);
    } catch (e) {
      throw new ApiError("El servidor devolvió una respuesta inesperada.", respuesta.status);
    }
  }

  if (!respuesta.ok) {
    const mensaje =
      (datos && datos.message) || "Ocurrió un error al comunicarse con el servidor.";
    throw new ApiError(mensaje, respuesta.status);
  }

  return datos;
}

// ------------------------------------------------------------
// Sesión
// ------------------------------------------------------------
export const sesion = {
  estado: () => pedir("check_session"),
  entrar: (username, password) =>
    pedir("login", { metodo: "POST", cuerpo: { username, password } }),
  salir: () => pedir("logout"),
};

// ------------------------------------------------------------
// Clientes
// ------------------------------------------------------------
export const clientes = {
  listar: ({ pagina = 1, limite = 6, busqueda = "", orden = "", filtro = "" } = {}) =>
    pedir("clientes", {
      params: { page: pagina, limit: limite, search: busqueda, orden, filtro },
    }),
  obtener: (id) => pedir("cliente", { params: { id } }),
  // El registro fijo al que se le cargan las ventas de quien no es
  // cliente del negocio.
  generico: () => pedir("cliente_generico"),
  crear: (datos) => pedir("clientes", { metodo: "POST", cuerpo: datos }),
  actualizar: (datos) => pedir("clientes", { metodo: "PUT", cuerpo: datos }),
  eliminar: (id) => pedir("clientes", { metodo: "DELETE", params: { id } }),
  saldos: (clienteId) => pedir("get_cliente_saldos", { params: { cliente_id: clienteId } }),
};

// ------------------------------------------------------------
// Ventas
// ------------------------------------------------------------
export const ventas = {
  listar: ({ clienteId, pagina = 1, limite = 5 }) =>
    pedir("ventas", { params: { cliente_id: clienteId, page: pagina, limit: limite } }),
  crear: (datos) => pedir("ventas", { metodo: "POST", cuerpo: datos }),
  actualizar: (datos) => pedir("ventas", { metodo: "PUT", cuerpo: datos }),
  eliminar: (id) => pedir("ventas", { metodo: "DELETE", params: { id } }),
  detalles: (ventaId) => pedir("venta_detalle", { params: { venta_id: ventaId } }),
};

// ------------------------------------------------------------
// Abonos
// ------------------------------------------------------------
export const abonos = {
  listar: ({ clienteId, pagina = 1, limite = 5 }) =>
    pedir("abonos", { params: { cliente_id: clienteId, page: pagina, limit: limite } }),
  crear: (datos) => pedir("abonos", { metodo: "POST", cuerpo: datos }),
  actualizar: (datos) => pedir("abonos", { metodo: "PUT", cuerpo: datos }),
  eliminar: (id) => pedir("abonos", { metodo: "DELETE", params: { id } }),
  detalle: (id) => pedir("get_abono_detalle", { params: { id } }),
};

// ------------------------------------------------------------
// Movimientos del día
//
// Devuelve las ventas y los abonos de un día mezclados en una sola
// lista, en orden de hora, y los totales ya calculados por el servidor.
// ------------------------------------------------------------
export const movimientos = {
  delDia: (fecha) => pedir("movimientos", { params: { date: fecha } }),
};

// Formas de pago que acepta el servidor, con su nombre para la pantalla.
export const FORMAS_PAGO = [
  { valor: "efectivo", nombre: "Efectivo" },
  { valor: "tarjeta", nombre: "Tarjeta" },
  { valor: "sinpe", nombre: "SINPE" },
  { valor: "transferencia", nombre: "Transferencia" },
];

export const nombreFormaPago = (valor) => {
  const forma = FORMAS_PAGO.find((f) => f.valor === valor);
  return forma ? forma.nombre : null;
};

export { ApiError };
